import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useForm } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Habilidade } from "../../../../domain/models/habilidade";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { defaultHabilidadeForm, HabilidadeForm, HabilidadeSchema } from "../../../schemas/Habilidades_lista/habilidade";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

/**
 * CadastroHabilidadeModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  selectedHabilidade: Habilidade | null,
  onSubmit: (form: HabilidadeForm) => void,
  onOpenChangeHistory: () => void,
  onClose: () => void,
};

export const CadastroHabilidadeModal = ({
  open,
  saving,
  selectedHabilidade,
  onSubmit,
  onOpenChangeHistory,
  onClose,
}: Props) => {
  const hookForm = useForm<HabilidadeForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: defaultHabilidadeForm,
    resolver: zodResolver(HabilidadeSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedHabilidade) {
        hookForm.setValue('habilidade', selectedHabilidade.nome ?? '');
        hookForm.setValue('descricao', selectedHabilidade.descricao ?? '');
      } else {
        hookForm.reset();
      }
    }
  }, [
    hookForm,
    open,
    selectedHabilidade,
  ]);

  const handleSubmit = useCallback((form: HabilidadeForm) => {
    if (!saving) onSubmit(form);
  }, [saving, onSubmit]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "85rem",
          },
        },
      }}>
      <DialogTitle>
        Cadastrar Habilidade
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <DefaultFormInput
                name='habilidade'
                label='Habilidade'
                readOnly={saving}
                required={true}
                reactHookForm={hookForm} />

              <DefaultFormInput
                name='descricao'
                label='Descrição'
                readOnly={saving}
                required={true}
                multiline={true}
                rows={10}
                reactHookForm={hookForm} />

            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex px-4 pb-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedHabilidade) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={onOpenChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>

            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}